import {
  action,
  computed,
  observable,
  reaction,
} from 'mobx';
import { RouterStore } from '@estee/elc-router';
import { INavigationItem } from '@estee/elc-navigation-header';
import {
  DELIVERY,
  PAYMENT,
} from '../../routes';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';
import UIState from '../../stores/UIState';
import ConfigRepository from '../../repositories/ConfigRepository';
import Config from '../../domain/entities/Config/Config';
import EditShippingAddressUseCase from '../../use-cases/EditShippingAddressUseCase';
import TransactionRepository from '../../repositories/TransactionRepository';
import Transaction from '../../domain/entities/Transaction/Transaction';
import formatPrice from '../../utils/PriceFormatUtils';
import { NAVIGATION_ITEM_IDS } from '../../constants/Constants';
import Routes, { ExternalRoute } from '../../application/routes/Routes';

interface IScreenLayoutConfig {
  analyticsRepository: AnalyticsRepository;
  stateRouter: RouterStore;
  configRepository: ConfigRepository;
  uiState: UIState;
  transactionRepository: TransactionRepository;
  routes: Routes;
}

interface IScreenLayoutViewControllerData {
  config: Config;
  isMobile: boolean;
  totalCost: number;
  navigationItems: INavigationItem[];
  currentNavItemdId: string;
  shouldShowPrivacyPolicy: boolean;
  privacyPolicyUrl: string;
  brandName: string;
  displayOurGuarantee: boolean;
}

class ScreenLayoutViewController {
  @observable currentStep = 0;

  @observable currentNavItemdId = '';

  @observable transaction: Transaction;

  @observable config: Config;

  analyticsRepository: AnalyticsRepository;

  configRepository: ConfigRepository;

  stateRouter: RouterStore;

  transactionRepository: TransactionRepository;

  uiState: UIState;

  routes: Routes;

  constructor(config: IScreenLayoutConfig) {
    this.analyticsRepository = config.analyticsRepository;
    this.stateRouter = config.stateRouter;
    this.configRepository = config.configRepository;
    this.transactionRepository = config.transactionRepository;
    this.routes = config.routes;

    this.uiState = config.uiState;

    config.uiState.updateItemActiveState(NAVIGATION_ITEM_IDS.deliveryId, false);
    config.uiState.updateItemActiveState(NAVIGATION_ITEM_IDS.paymentId, false);

    reaction(() => this.stateRouter.currentRoute,
      (currentRoute: string) => this.setCurrentStep(currentRoute),
      { fireImmediately: true });
  }

  @action
  async loaded(): Promise<void> {
    const config = await this.configRepository.getConfig();
    const transaction = await this.transactionRepository.getTransaction();

    this.setConfig(config);
    this.setTransaction(transaction);

    reaction(() => ({
      billingAddressIsMissing: this.transaction.billingAddress.isNewAddress,
      shippingAddressIsMissing: this.transaction.shippingAddress.isNewAddress,
    }),
    ({
      billingAddressIsMissing,
      shippingAddressIsMissing,
    }: {
      billingAddressIsMissing: boolean;
      shippingAddressIsMissing: boolean;
    }) => {
      const isAddressMissing = shippingAddressIsMissing || billingAddressIsMissing;
      if (this.stateRouter.currentRoute === DELIVERY.ROUTE && isAddressMissing) {
        const paymentNavItem = this.navigationItems.find(({ href }) => href === `#/${PAYMENT.ROUTE}`);
        paymentNavItem.isActive = false;
      }
    });
  }

  @action
  setConfig = (config: Config): void => {
    this.config = config;
  };

  @action
  setTransaction = (transaction: Transaction): void => {
    this.transaction = transaction;
  };

  @computed
  get navigationItems(): INavigationItem[] {
    let navigationItems = this.uiState.navigationItems;
    if (this.transaction?.isZeroPaymentCheckout) {
      navigationItems = navigationItems.filter(item => item.id !== NAVIGATION_ITEM_IDS.paymentId);
    }
    return navigationItems;
  }
  
  @computed
  get data(): IScreenLayoutViewControllerData {
    return {
      brandName: this.config?.brandName,
      config: this.config,
      currentNavItemdId: this.currentNavItemdId,
      displayOurGuarantee: this.displayOurGuarantee,
      isMobile: this.uiState.isMobile,
      navigationItems: this.navigationItems,
      privacyPolicyUrl: this.config?.privacyPolicyUrl,
      shouldShowPrivacyPolicy: this.shouldShowPrivacyPolicy,
      totalCost: this.transaction && this.transaction.orderSummary.totalCost,
    };
  }

  @computed
  get shouldShowPrivacyPolicy(): boolean {
    return this.uiState.isMobile && this.stateRouter.currentRoute === DELIVERY.ROUTE;
  }

  onClickBack = (): void => {
    if (this.stateRouter.currentRoute === PAYMENT.ROUTE) {
      this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE);
    } else if (this.stateRouter.currentRoute === DELIVERY.ROUTE) {
      this.navigateToViewCart();
    }
  };

  @action
  setCurrentStep(routeName: string): void {
    const navRouteIndex = this.navigationItems.findIndex(item => item.href === `#/${routeName}`);

    if (navRouteIndex >= 0) {
      const navItem = this.navigationItems[navRouteIndex];
      navItem.isActive = true;
      if (this.currentNavItemdId !== navItem.id) {
        this.currentNavItemdId = navItem.id;
        /* eslint-disable no-param-reassign */
        this.navigationItems.forEach((item, index) => {
          if (index === navRouteIndex) {
            item.isActive = true;
            item.isCompleted = false;
          } else if (index < navRouteIndex) {
            item.isActive = true;
            item.isCompleted = true;
          } else {
            item.isCompleted = false;
          }
        });
      }
    }
  }

  onEditShipping = async (): Promise<void> => {
    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {}, { inEditMode: 1 });

    if (this.transaction && this.transaction.user.isUserRegistered) {
      const useCase = new EditShippingAddressUseCase(
        this.transaction.shippingAddress.id,
        this.transaction.isSameAsShipping,
      );
      useCase.execute();
    }
  };
  
  onEditGiftWrap = (): void => {
    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {});
  };

  navigateToCompanyBillingDetails = (): void => {
    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {}, { companyBillingInEditMode: 1 });
  };

  navigateToViewCart(): void {
    this.routes.redirect(ExternalRoute.CART);
  }

  getPrice = (price: number): string => this.config && formatPrice(this.config, price);

  @computed
  private get displayOurGuarantee(): boolean {
    if (this.stateRouter.currentRoute === DELIVERY.ROUTE) {
      return this.config?.displayOurGuaranteeOn.deliveryPage;
    } else if (this.stateRouter.currentRoute === PAYMENT.ROUTE) {
      return this.config?.displayOurGuaranteeOn.paymentPage;
    }
  }
}

export default ScreenLayoutViewController;
